// TrackTile.jsx
import React, { useEffect, useState } from 'react';
import { Track } from 'livekit-client';
import { VideoTrack } from '@livekit/components-react';
import { isRenderableScreenShareTrack } from '../screenShareUtils';

/** Get initials from participant identity (e.g. "John Doe" -> "JD", "alice" -> "A") */
function getInitials(identity) {
  if (!identity || typeof identity !== 'string') return '?';
  const parts = identity.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
  }
  return identity.slice(0, 2).toUpperCase() || '?';
}

/** Stable color index from string for avatar background */
function getAvatarColorIndex(str) {
  if (!str) return 0;
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h << 5) - h + str.charCodeAt(i) | 0;
  return Math.abs(h) % 6;
}

const AVATAR_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#3b82f6'];

export default function TrackTile({
  track,
  participant: participantProp,
  isActive,
  onClick,
  networkLevel = 0,
  isAudioMuted,
  isVideoMuted,
  isScreenSharing = false,
  variant = 'default',
  showPinHint = true,
}) {
  const participant = track?.participant ?? participantProp;
  const isScreenShare = variant === 'screen';
  const [, setEndedBump] = useState(0);
  const screenMst =
    track?.publication?.videoTrack?.mediaStreamTrack ?? track?.publication?.track?.mediaStreamTrack;
  useEffect(() => {
    if (!isScreenShare || !screenMst) return undefined;
    const onEnded = () => setEndedBump((n) => n + 1);
    screenMst.addEventListener('ended', onEnded);
    return () => screenMst.removeEventListener('ended', onEnded);
  }, [isScreenShare, screenMst]);
  const audioOff = isAudioMuted === true;
  const videoOff = isScreenShare
    ? !isRenderableScreenShareTrack(track)
    : isVideoMuted === true || !track;
  const identity = participant?.identity ?? 'Unknown';
  const initials = getInitials(identity);
  const avatarColor = AVATAR_COLORS[getAvatarColorIndex(identity)];

  return (
    <div
      className={`track-tile ${isActive ? 'active-speaker' : ''} ${videoOff ? 'track-tile--video-off' : ''} ${isScreenShare ? 'track-tile--screen-share' : ''} ${showPinHint ? '' : 'track-tile--no-pin'}`}
      onClick={onClick}
      title={identity}
      aria-label={`Participant: ${identity}`}
    >
      <span className="track-tile__hover-name">{identity}</span>
      {videoOff ? (
        <div className="track-tile__avatar" style={{ backgroundColor: avatarColor }}>
          <span className="track-tile__avatar-initials">{initials}</span>
        </div>
      ) : track ? (
        <VideoTrack
          trackRef={track}
          participant={participant}
          source={isScreenShare ? Track.Source.ScreenShare : Track.Source.Camera}
          manageSubscription={isScreenShare ? false : undefined}
        />
      ) : (
        <div className="track-tile__avatar" style={{ backgroundColor: avatarColor }}>
          <span className="track-tile__avatar-initials">{initials}</span>
        </div>
      )}
      {isActive && (
        <span className="track-tile__speaking" aria-live="polite">
          Speaking
        </span>
      )}
      <span className={`name-badge ${isActive ? 'name-badge--speaking' : ''}`}>
        {identity}
      </span>
      {/* Mic / cam / screen — always visible (z-index above avatar) */}
      <div className="track-tile__media-status" aria-hidden="true">
        <span
          className={`track-tile__status track-tile__status--mic ${audioOff ? 'track-tile__status--bad' : 'track-tile__status--ok'}`}
          title={audioOff ? 'Microphone off' : 'Microphone on'}
        >
          {audioOff ? '🎤 off' : '🎤 on'}
        </span>
        {!isScreenShare && (
          <span
            className={`track-tile__status track-tile__status--cam ${videoOff ? 'track-tile__status--bad' : 'track-tile__status--ok'}`}
            title={videoOff ? 'Camera off' : 'Camera on'}
          >
            {videoOff ? '📷 off' : '📷 on'}
          </span>
        )}
        {isScreenSharing && (
          <span className="track-tile__status track-tile__status--share" title="Sharing screen">
            🖥 share
          </span>
        )}
      </div>
      {/* Network quality */}
      <div className={`net-quality q${Math.min(4, Math.max(0, networkLevel))}`} title="Connection quality">
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}
